/**
 * Course Detail Page
 *
 * Displays comprehensive information about a specific CPR course including:
 * - Course overview and what you'll learn
 * - Pricing table with all options
 * - Locations offered
 * - What's included
 * - Target audience
 * - Related courses
 * BRUTALIST STREETWEAR DESIGN SYSTEM
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getCourseBySlug, courses } from '../../data/courses';
import PricingTable from '../../components/cpr/PricingTable';
import CourseCard from '../../components/cpr/CourseCard';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Course } from '../../types/course';

const CourseDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const course = slug ? getCourseBySlug(slug) : undefined;

  // Redirect to 404 if course not found
  useEffect(() => {
    if (!course) {
      navigate('/404', { replace: true });
    }
  }, [course, navigate]);

  // Don't render anything while redirecting
  if (!course) {
    return null;
  }

  // Get related courses (other courses, max 3)
  const relatedCourses = courses
    .filter((c) => c.id !== course.id)
    .slice(0, 3);

  const handleRelatedCourseClick = (relatedCourse: Course) => {
    navigate(`/courses/${relatedCourse.slug}`);
    // Scroll to top when navigating to new course
    window.scrollTo(0, 0);
  };

  // Format course formats for display
  const formatCourseFormats = (formats: string[]): string => {
    return formats
      .map((format) => {
        switch (format) {
          case 'in-person':
            return 'In-Person';
          case 'blended':
            return 'Blended Learning';
          case 'online':
            return 'Online';
          case 'on-site':
            return 'On-Site';
          default:
            return format;
        }
      })
      .join(', ');
  };

  // Generate Course Schema for structured data
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.name,
    "description": course.description,
    "provider": {
      "@type": "Organization",
      "name": "CPR Training Direct",
      "sameAs": "https://cprtrainingdirect.com"
    },
    "hasCourseInstance": course.locations
      .filter(loc => loc.available)
      .map(location => ({
        "@type": "CourseInstance",
        "courseMode": course.formats.includes('on-site') ? "On-Site" : "In-Person",
        "duration": course.duration.includes('hour') ? `PT${course.duration.split('-')[0].trim().replace(' hour', 'H')}` : "PT4H",
        "instructor": {
          "@type": "Person",
          "name": "Certified CPR Instructor"
        },
        "location": {
          "@type": "Place",
          "name": `CPR Training Direct - ${location.city}`,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": location.city,
            "addressRegion": location.state,
            "addressCountry": "US"
          }
        }
      })),
    "offers": {
      "@type": "Offer",
      "price": course.pricing[0].amount.toString(),
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": `https://cprtrainingdirect.com/courses/${course.slug}`,
      "validFrom": "2025-01-01"
    },
    "educationalCredentialAwarded": `${course.name} - ${course.certification} Certification`,
    "timeRequired": course.duration.includes('hour') ? `PT${course.duration.split('-')[0].trim().replace(' hour', 'H')}` : "PT4H",
    "inLanguage": "en-US"
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags & Schema.org Structured Data */}
      <Helmet>
        <title>{course.name} | CPR Training Direct</title>
        <meta name="description" content={course.description} />
        <meta name="keywords" content={`${course.name}, CPR certification, AED training, ${course.certification}, ${course.locations.map(l => l.city).join(', ')}`} />
        <link rel="canonical" href={`https://cprtrainingdirect.com/courses/${course.slug}`} />

        {/* Schema.org Structured Data - Course */}
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      </Helmet>

      {/* Hero Section - Brutalist */}
      <div className="bg-indigo_dye-500 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div data-aos="zoom-in" className="text-7xl md:text-8xl mb-6">{course.icon}</div>
            <h1
              data-aos="fade-up"
              data-aos-delay="100"
              className="font-display text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider mb-6"
            >
              {course.name}
            </h1>
            <p
              data-aos="fade-up"
              data-aos-delay="200"
              className="font-body text-xl md:text-2xl text-indigo_dye-100 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              {course.tagline}
            </p>
            <div
              data-aos="fade-up"
              data-aos-delay="300"
              className="flex flex-wrap justify-center gap-6 text-base md:text-lg"
            >
              <div className="bg-white/10 border-2 border-white/30 px-6 py-3 backdrop-blur-sm">
                <span className="font-mono uppercase tracking-wider font-bold">Duration:</span>
                <span className="ml-2 font-body">{course.duration}</span>
              </div>
              <div className="bg-white/10 border-2 border-white/30 px-6 py-3 backdrop-blur-sm">
                <span className="font-mono uppercase tracking-wider font-bold">Certification:</span>
                <span className="ml-2 font-body">{course.certification}</span>
              </div>
              <div className="bg-white/10 border-2 border-white/30 px-6 py-3 backdrop-blur-sm">
                <span className="font-mono uppercase tracking-wider font-bold">Valid:</span>
                <span className="ml-2 font-body">{course.certificationValidity}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Description */}
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div data-aos="fade-up">
        <Card variant="brutalist" className="mb-12">
          <p className="font-body text-lg text-gray-700 leading-relaxed">
            {course.description}
          </p>
        </Card>
        </div>

        {/* What You'll Learn */}
        <div data-aos="fade-up" data-aos-delay="100">
        <Card variant="brutalist" title="WHAT YOU'LL LEARN" className="mb-12">
          <ul className="grid md:grid-cols-2 gap-4">
            {course.whatYouLearn.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-amaranth_purple-500 flex-shrink-0 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="font-body text-gray-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </Card>
        </div>

        {/* Course Information Grid */}
        <div data-aos="fade-up">
        <Card variant="brutalist" title="COURSE INFORMATION" className="mb-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-gray-900 mb-3">
                Course Duration
              </h3>
              <p className="font-body text-gray-700">{course.duration}</p>
            </div>
            <div>
              <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-gray-900 mb-3">
                Certification Organization
              </h3>
              <p className="font-body text-gray-700">
                {course.certification} ({course.certificationValidity} validity)
              </p>
            </div>
            <div>
              <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-gray-900 mb-3">
                Course Formats
              </h3>
              <p className="font-body text-gray-700">
                {formatCourseFormats(course.formats)}
              </p>
            </div>
            <div>
              <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-gray-900 mb-3">
                Prerequisites
              </h3>
              <p className="font-body text-gray-700">
                {course.prerequisites || 'None required'}
              </p>
            </div>
          </div>
        </Card>
        </div>

        {/* Pricing Table */}
        <div data-aos="fade-up">
        <Card variant="brutalist" title="PRICING OPTIONS" className="mb-12">
          <PricingTable pricing={course.pricing} />
          <p className="font-body text-sm text-gray-600 mt-6 uppercase tracking-wide">
            All prices are in USD. Group rates available for 5+ participants.
          </p>
        </Card>
        </div>

        {/* Locations Offered */}
        <div data-aos="fade-up">
        <Card variant="brutalist" title="LOCATIONS OFFERED" className="mb-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {course.locations.map((location, index) => (
              <div
                key={index}
                className={`p-4 border-4 ${
                  location.available
                    ? 'border-amaranth_purple-500 bg-amaranth_purple-50'
                    : 'border-gray-300 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono font-bold uppercase text-gray-900">
                      {location.city}, {location.state}
                    </div>
                    <div className="font-body text-sm mt-2">
                      {location.available ? (
                        <span className="text-amaranth_purple-600 font-semibold uppercase tracking-wide">
                          Available
                        </span>
                      ) : (
                        <span className="text-gray-500 uppercase tracking-wide">Coming Soon</span>
                      )}
                    </div>
                  </div>
                  {location.available && (
                    <svg
                      className="w-8 h-8 text-amaranth_purple-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
        </div>

        {/* What's Included */}
        <div data-aos="fade-up">
        <Card variant="brutalist" title="WHAT'S INCLUDED" className="mb-12">
          <ul className="grid md:grid-cols-2 gap-4">
            {course.whatsIncluded.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-indigo_dye-500 flex-shrink-0 mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-body text-gray-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </Card>
        </div>

        {/* Who Should Take This */}
        <div data-aos="fade-up">
        <Card variant="brutalist" title="WHO SHOULD TAKE THIS COURSE" className="mb-12">
          <p className="font-body text-lg text-gray-700 leading-relaxed">
            {course.whoShouldTake}
          </p>
        </Card>
        </div>

        {/* CTA Section - Brutalist */}
        <div
          data-aos="fade-up"
          className="bg-amaranth_purple-500 border-4 border-amaranth_purple-700 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] p-12 mb-12 text-center text-white"
        >
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-wider mb-6">
            Ready to Get Certified?
          </h2>
          <p className="font-body text-xl md:text-2xl text-amaranth_purple-100 mb-8">
            Book your class today and receive same-day certification!
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button
              size="xl"
              onClick={() => navigate('/book')}
              className="bg-white border-4 border-white !text-gray-900 hover:bg-amaranth_purple-900 hover:!text-white hover:border-amaranth_purple-900 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] hover:shadow-[12px_12px_0px_0px_rgba(2,5,18,1)]"
            >
              BOOK THIS CLASS
            </Button>
            <Button
              size="xl"
              onClick={() => navigate('/contact')}
              className="bg-amaranth_purple-700 border-4 border-white text-white hover:bg-white hover:text-amaranth_purple-600 hover:border-white shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] hover:shadow-[12px_12px_0px_0px_rgba(2,5,18,1)]"
            >
              CONTACT US
            </Button>
          </div>
        </div>

        {/* Related Courses */}
        {relatedCourses.length > 0 && (
          <div data-aos="fade-up" className="mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-wider text-indigo_dye-500 mb-8">
              Other Courses You Might Like
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedCourses.map((relatedCourse) => (
                <CourseCard
                  key={relatedCourse.id}
                  course={relatedCourse}
                  onClick={handleRelatedCourseClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Back to Courses Link */}
        <div className="text-center mt-12">
          <Link
            to="/courses"
            className="inline-flex items-center gap-3 px-8 py-4 bg-white border-4 border-indigo_dye-500 text-indigo_dye-600 font-mono uppercase tracking-wider font-bold shadow-[4px_4px_0px_0px_rgba(2,5,18,1)] hover:shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] hover:-translate-x-1 hover:-translate-y-1 transition-all"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to All Courses
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
